import React, { useMemo, useState } from 'react';

/** Classes Tailwind courantes pour le texte — noms littéraux pour le JIT Tailwind */
export const BUILDING_TAILWIND_TEXT_COLORS: readonly string[] = [
  'text-red-400',
  'text-red-500',
  'text-orange-400',
  'text-amber-400',
  'text-yellow-400',
  'text-lime-400',
  'text-green-400',
  'text-emerald-400',
  'text-teal-400',
  'text-cyan-400',
  'text-sky-400',
  'text-blue-400',
  'text-indigo-400',
  'text-violet-400',
  'text-cockpit-violet',
  'text-fuchsia-400',
  'text-pink-400',
  'text-rose-400',
  'text-cockpit-text-2',
  'text-cockpit-text-2',
  'text-cockpit-muted',
  'text-cockpit-text',
];

export const BUILDING_ICON_EMOJIS: readonly string[] = [
  '🚒',
  '🏥',
  '🚔',
  '🚓',
  '🚨',
  '🚑',
  '🚁',
  '🏢',
  '🏛️',
  '🏗️',
  '🔒',
  '📞',
  '🧯',
  '🏘️',
  '🛣️',
  '⛺',
  '🧑‍🚒',
  '🏕️',
  '⚕️',
  '🆘',
];

export interface BuildingAppearanceLabels {
  iconLabel: string;
  iconPlaceholder: string;
  iconPickHint: string;
  couleurLabel: string;
  couleurPlaceholder: string;
  colorPickHint: string;
  customTailwind: string;
}

interface BuildingAppearanceFieldsProps {
  icon: string;
  couleur: string;
  onIconChange: (value: string) => void;
  onCouleurChange: (value: string) => void;
  labels: BuildingAppearanceLabels;
}

const BuildingAppearanceFields: React.FC<BuildingAppearanceFieldsProps> = ({
  icon,
  couleur,
  onIconChange,
  onCouleurChange,
  labels,
}) => {
  const [showCustomColor, setShowCustomColor] = useState(false);
  const normalizedCouleur = (couleur || '').trim();

  const colorIsPreset = useMemo(
    () => BUILDING_TAILWIND_TEXT_COLORS.includes(normalizedCouleur),
    [normalizedCouleur]
  );

  return (
    <>
      <div className="md:col-span-2 space-y-2">
        <label className="block text-xs font-medium text-cockpit-text-2 mb-1.5">{labels.iconLabel}</label>
        <p className="text-xs text-cockpit-muted mb-2">{labels.iconPickHint}</p>
        <div className="flex flex-wrap gap-2 p-2 rounded-lg border border-[#223046] bg-[#0b1220]/80">
          {BUILDING_ICON_EMOJIS.map((em) => (
            <button
              key={em}
              type="button"
              title={em}
              onClick={() => onIconChange(em)}
              className={`text-2xl leading-none w-11 h-11 rounded-lg border transition-colors flex items-center justify-center ${
                icon === em
                  ? 'border-amber-500/60 bg-amber-500/20 ring-1 ring-amber-500/40'
                  : 'border-[#223046] bg-[#0b1220] hover:border-[#6366f1]/40'
              }`}
            >
              {em}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={icon}
          onChange={(e) => onIconChange(e.target.value)}
          placeholder={labels.iconPlaceholder}
          className="mt-2 w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60"
        />
      </div>

      <div className="md:col-span-2 space-y-2">
        <label className="block text-xs font-medium text-cockpit-text-2 mb-1.5">{labels.couleurLabel}</label>
        <p className="text-xs text-cockpit-muted mb-2">{labels.colorPickHint}</p>
        <div className="flex flex-wrap gap-2 p-2 rounded-lg border border-[#223046] bg-[#0b1220]/80">
          {BUILDING_TAILWIND_TEXT_COLORS.map((cls) => (
            <button
              key={cls}
              type="button"
              title={cls}
              onClick={() => {
                onCouleurChange(cls);
                setShowCustomColor(false);
              }}
              className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-sm font-bold transition-colors ${
                normalizedCouleur === cls
                  ? 'border-amber-400 ring-2 ring-amber-500/40 scale-105'
                  : 'border-[#223046] hover:border-[#6366f1]/40'
              } ${cls}`}
            >
              Aa
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowCustomColor((v) => !v)}
          className="text-xs text-[#c7d2fe] hover:text-[#c7d2fe] underline"
        >
          {labels.customTailwind}
        </button>
        {(showCustomColor || (normalizedCouleur && !colorIsPreset)) && (
          <input
            type="text"
            value={couleur}
            onChange={(e) => onCouleurChange(e.target.value)}
            placeholder={labels.couleurPlaceholder}
            className="mt-2 w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60 font-mono"
          />
        )}
      </div>
    </>
  );
};

export default BuildingAppearanceFields;
